import { brandPalette } from "../../../../constants/brandPalette";
import {
  Badge,
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  HStack,
  Image,
  Input,
  Progress,
  Radio,
  RadioGroup,
  SimpleGrid,
  Stack,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  VStack,
} from "@chakra-ui/react";
import dayjs from "dayjs";
import ExcelJS from "exceljs";
import React, { useEffect, useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { RiFileExcel2Line } from "react-icons/ri";
import { useDispatch, useSelector } from "react-redux";
import Select from "react-select";
import { toast } from "react-toastify";
import MainInput from "../../../../components/Form/input";
import MainModel from "../../../../components/Models/main-model";
import {
  adminModalBodyProps,
  adminModalContentProps,
  adminModalHeaderProps,
  adminSelectStyles,
  withAdminSelectMenuPortal,
} from "../../../../components/Models/admin-modal-theme";
import { getAllChaptersRequest } from "../../../../modules/admin-modules/chapters/Actions";
import { getAllExamsRequest } from "../../../../modules/admin-modules/exams/Actions";
import { getAllGroupsRequest } from "../../../../modules/admin-modules/groups/Actions";
import { closeBulkImportVideosModel } from "../../../../modules/admin-modules/models/Actions";
import { createVideo } from "../../../../modules/admin-modules/videos/Api";
import {
  getAllVideosRequest,
  getPaymentSubjectRequest,
} from "../../../../modules/admin-modules/videos/Actions";

const REQUEST_DELAY_MS = 1200;

const HEADER_ALIASES = {
  title: ["title"],
  url: ["url", "video_url", "link"],
  over_view: ["over_view", "overview"],
};

const sectionShell = {
  borderRadius: "12px",
  border: "1px solid",
  borderColor: "var(--border-subtle)",
  bg: "var(--surface-muted)",
  p: { base: 4, md: 5 },
};

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const normalizeHeader = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_")
    .replace(/[^a-z0-9_]/g, "");

const cellToString = (cell) => {
  if (!cell) return "";
  const value = cell.value;
  if (value == null || value === "") return "";
  if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") {
    return String(value).trim();
  }
  if (value instanceof Date) return value.toISOString();
  if (typeof value === "object") {
    if (typeof value.hyperlink === "string" && value.hyperlink) {
      return value.hyperlink.trim();
    }
    if (typeof value.text === "string" && value.text.trim()) {
      return value.text.trim();
    }
    if (Array.isArray(value.richText)) {
      return value.richText.map((part) => part?.text || "").join("").trim();
    }
    if (value.result != null && value.result !== "") {
      return String(value.result).trim();
    }
  }
  return String(value).trim();
};

const resolveColumn = (colMap, aliases) => {
  for (const alias of aliases) {
    if (colMap[alias]) return colMap[alias];
  }
  return null;
};

const getApiError = (error) => {
  const data = error?.response?.data;
  if (!data) return error?.message || "Request failed";
  if (typeof data.message === "string" && data.message) return data.message;
  if (data.errors && typeof data.errors === "object") {
    return Object.values(data.errors).flat().join(" ");
  }
  return "Failed to create video";
};

const parseVideosSheet = async (file) => {
  const workbook = new ExcelJS.Workbook();
  await workbook.xlsx.load(await file.arrayBuffer());
  const worksheet = workbook.worksheets[0];
  if (!worksheet) {
    throw new Error("The Excel file has no worksheet.");
  }

  const colMap = {};
  worksheet.getRow(1).eachCell((cell, colNumber) => {
    const key = normalizeHeader(cellToString(cell) || cell.value);
    if (key) colMap[key] = colNumber;
  });

  const titleCol = resolveColumn(colMap, HEADER_ALIASES.title);
  const urlCol = resolveColumn(colMap, HEADER_ALIASES.url);
  const overviewCol = resolveColumn(colMap, HEADER_ALIASES.over_view);

  if (!titleCol || !urlCol) {
    throw new Error("Sheet must include title and url columns.");
  }

  const rows = [];
  worksheet.eachRow((row, rowNumber) => {
    if (rowNumber === 1) return;
    const title = cellToString(row.getCell(titleCol));
    const url = cellToString(row.getCell(urlCol));
    const over_view = overviewCol ? cellToString(row.getCell(overviewCol)) : "";
    if (!title && !url && !over_view) return;
    rows.push({
      id: `row-${rowNumber}-${rows.length}`,
      rowNumber,
      title,
      url,
      over_view,
      valid: Boolean(title && url),
      status: "pending",
      error: "",
    });
  });

  if (!rows.length) {
    throw new Error("No video rows found in the sheet.");
  }

  return rows;
};

const buildVideoFormData = (values, row) => {
  const formData = new FormData();
  const expiry = values?.expiry_date
    ? dayjs(values.expiry_date).format("YYYY-MM-DD")
    : dayjs().add(1, "week").format("YYYY-MM-DD");

  formData.append("expiry_date", expiry);
  formData.append("title", row.title);
  formData.append("url", row.url);
  formData.append("over_view", row.over_view || "over view");

  const skip = new Set(["title", "url", "over_view", "expiry_date", "groups", "thumbnail"]);
  Object.keys(values || {}).forEach((key) => {
    if (skip.has(key)) return;
    const value = values[key];
    if (value !== undefined && value !== null && value !== "") {
      formData.append(key, value);
    }
  });

  (values?.groups || []).forEach((id, idx) => {
    formData.append(`groups[${idx}]`, id);
  });

  if (values?.thumbnail instanceof File) {
    formData.set("thumbnail", values.thumbnail);
  }

  return formData;
};

const statusBadge = (row) => {
  if (!row.valid && row.status === "pending") {
    return <Badge colorScheme="orange">Missing title or URL</Badge>;
  }
  if (row.status === "importing") {
    return <Badge colorScheme="blue">Importing</Badge>;
  }
  if (row.status === "success") {
    return <Badge colorScheme="green">Created</Badge>;
  }
  if (row.status === "error") {
    return <Badge colorScheme="red">Failed</Badge>;
  }
  return <Badge>Ready</Badge>;
};

const BulkImportVideos = () => {
  const dispatch = useDispatch();
  const openModal = useSelector((state) => state.model.isOpen46);
  const chapters = useSelector((state) => state.chapters.chapters);
  const groups = useSelector((state) => state.groups.groups);
  const paymentSubjects = useSelector((state) => state.videos.paymentSubjects);
  const exams = useSelector((state) => state.exams.exams);

  const oneWeekFromNow = dayjs().add(1, "week").format("YYYY-MM-DDTHH:mm");

  const [fileName, setFileName] = useState("");
  const [rows, setRows] = useState([]);
  const [isImporting, setIsImporting] = useState(false);
  const [progress, setProgress] = useState({
    done: 0,
    total: 0,
    success: 0,
    failed: 0,
  });
  const [totalPlayed, setTotalPlayed] = useState(true);
  const [thumbLabel, setThumbLabel] = useState("");
  const [thumbPreview, setThumbPreview] = useState("");

  const {
    register,
    control,
    handleSubmit,
    setValue,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: {
      publish: "1",
      video_type: "1",
      auto_controlled: "2",
      type: "1",
      related_to_homework: "0",
      total_played: 2,
      expiry_date: oneWeekFromNow,
    },
  });

  useEffect(() => {
    register("chapter_id");
    register("groups");
    register("payment_subject_id");
    register("post_exam_id");
    register("thumbnail");
  }, [register]);

  const groupsOptions = useMemo(
    () =>
      (groups || []).map((data) => ({
        value: data.id,
        label: data.name,
      })),
    [groups]
  );

  const chaptersOptions = useMemo(
    () =>
      (Array.isArray(chapters) ? chapters : [])
        .filter((c) => Number(c?.active) === 1)
        .map((data) => ({ value: data.id, label: data.name })),
    [chapters]
  );

  const paymentSubjectsOptions = useMemo(
    () =>
      (paymentSubjects || []).map((data) => ({
        value: data.id,
        label: data.name,
      })),
    [paymentSubjects]
  );

  const examsOptions = useMemo(
    () =>
      (exams || []).map((data) => ({
        value: data.id,
        label: data.name,
      })),
    [exams]
  );

  const selectStyles = useMemo(
    () =>
      withAdminSelectMenuPortal(adminSelectStyles(), {
        zIndex: 9999,
        menuMaxHeight: "240px",
      }),
    []
  );
  const selectMenuPortalProps = {
    menuPortalTarget: typeof document !== "undefined" ? document.body : null,
    menuPosition: "fixed",
    menuShouldScrollIntoView: false,
    menuShouldBlockScroll: false,
    closeMenuOnSelect: true,
  };

  useEffect(() => {
    if (openModal) {
      if (!chapters?.length) dispatch(getAllChaptersRequest());
      if (!groups?.length) dispatch(getAllGroupsRequest());
      dispatch(getPaymentSubjectRequest());
      dispatch(getAllExamsRequest());
    }
  }, [openModal, chapters?.length, groups?.length, dispatch]);

  useEffect(() => {
    return () => {
      if (thumbPreview) URL.revokeObjectURL(thumbPreview);
    };
  }, [thumbPreview]);

  const validRows = rows.filter((row) => row.valid);
  const remainingRows = validRows.filter((row) => row.status !== "success");
  const failedRows = rows.filter((row) => row.status === "error");
  const progressPercent =
    progress.total > 0 ? Math.round((progress.done / progress.total) * 100) : 0;

  const sectionLabel = (label) => (
    <Text
      fontSize="xs"
      fontWeight="800"
      color="var(--text-muted)"
      textTransform="uppercase"
      letterSpacing="0.12em"
      mb={3}
    >
      {label}
    </Text>
  );

  const resetState = () => {
    setFileName("");
    setRows([]);
    setIsImporting(false);
    setProgress({ done: 0, total: 0, success: 0, failed: 0 });
    setTotalPlayed(true);
    setThumbLabel("");
    if (thumbPreview) URL.revokeObjectURL(thumbPreview);
    setThumbPreview("");
    reset({
      publish: "1",
      video_type: "1",
      auto_controlled: "2",
      type: "1",
      related_to_homework: "0",
      total_played: 2,
      expiry_date: dayjs().add(1, "week").format("YYYY-MM-DDTHH:mm"),
    });
  };

  const closeFunc = () => {
    if (isImporting) {
      toast.info("Please wait until the import finishes.");
      return;
    }
    dispatch(closeBulkImportVideosModel());
    resetState();
  };

  const handleFile = async (file) => {
    if (!file || isImporting) return;
    try {
      const parsed = await parseVideosSheet(file);
      setFileName(file.name);
      setRows(parsed);
      setProgress({ done: 0, total: 0, success: 0, failed: 0 });
      toast.success(`Loaded ${parsed.length} row${parsed.length === 1 ? "" : "s"}`);
    } catch (error) {
      setFileName("");
      setRows([]);
      toast.error(error?.message || "Failed to read Excel file.");
    }
  };

  const updateRow = (id, patch) => {
    setRows((prev) =>
      prev.map((row) => {
        if (row.id !== id) return row;
        const next = { ...row, ...patch };
        next.valid = Boolean(next.title && next.url);
        return next;
      })
    );
  };

  const runImport = async (values, targetRows) => {
    if (!values?.chapter_id || !values?.groups?.length) {
      toast.error("chapter and groups required");
      return;
    }

    const toImport = targetRows.filter((row) => row.valid);
    if (!toImport.length) {
      toast.error("No valid rows to import. Each row needs a title and url.");
      return;
    }

    setIsImporting(true);
    setProgress({ done: 0, total: toImport.length, success: 0, failed: 0 });

    let success = 0;
    let failed = 0;

    for (let i = 0; i < toImport.length; i += 1) {
      const row = toImport[i];
      setRows((prev) =>
        prev.map((item) =>
          item.id === row.id ? { ...item, status: "importing", error: "" } : item
        )
      );

      try {
        await createVideo(buildVideoFormData(values, row));
        success += 1;
        setRows((prev) =>
          prev.map((item) =>
            item.id === row.id ? { ...item, status: "success", error: "" } : item
          )
        );
      } catch (error) {
        failed += 1;
        const message = getApiError(error);
        setRows((prev) =>
          prev.map((item) =>
            item.id === row.id ? { ...item, status: "error", error: message } : item
          )
        );
      }

      setProgress({
        done: i + 1,
        total: toImport.length,
        success,
        failed,
      });

      if (i < toImport.length - 1) {
        await sleep(REQUEST_DELAY_MS);
      }
    }

    setIsImporting(false);
    dispatch(getAllVideosRequest());

    if (failed === 0) {
      toast.success(`Imported ${success} video${success === 1 ? "" : "s"}`);
    } else {
      toast.warn(`Imported ${success} of ${toImport.length}. ${failed} failed.`);
    }
  };

  const onSubmit = (values) => {
    const pendingOrFailed = rows.filter(
      (row) => row.status === "pending" || row.status === "error"
    );
    runImport(values, pendingOrFailed);
  };

  const header = (
    <Box>
      <Heading
        fontSize="32px"
        fontWeight="800"
        color="var(--text-primary)"
        fontFamily="'Amiri','Tajawal',sans-serif"
      >
        Import videos from Excel
      </Heading>
      <Text
        fontSize="xs"
        mt={1}
        color="var(--text-muted)"
        textTransform="uppercase"
        letterSpacing="0.14em"
        fontWeight="700"
      >
        Upload title, over_view, and url — then apply the same session options to every row
      </Text>
    </Box>
  );

  return (
    <MainModel
      isLoading={isImporting}
      header={header}
      closeFunc={closeFunc}
      openVar={openModal}
      buttonTitle={null}
      size="6xl"
      contentProps={{ ...adminModalContentProps, overflow: "visible" }}
      headerProps={adminModalHeaderProps}
      bodyProps={{ ...adminModalBodyProps, maxH: "80vh", overflowY: "auto", overflowX: "visible" }}
      hideDefaultFooter
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <Stack spacing={4} dir="ltr" w="100%">
          <Box {...sectionShell}>
            {sectionLabel("Excel file")}
            <Flex gap={3} flexWrap="wrap" align="center">
              <Button
                leftIcon={<RiFileExcel2Line />}
                bg={brandPalette.primary}
                color="white"
                borderRadius="lg"
                position="relative"
                fontWeight="700"
                isDisabled={isImporting}
                _hover={{ bg: brandPalette.accentDark }}
              >
                {fileName || "Choose Excel file"}
                <Input
                  type="file"
                  accept=".xlsx"
                  opacity="0"
                  position="absolute"
                  w="100%"
                  h="100%"
                  left={0}
                  top={0}
                  cursor={isImporting ? "not-allowed" : "pointer"}
                  disabled={isImporting}
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    handleFile(file);
                    e.target.value = "";
                  }}
                />
              </Button>
            </Flex>
            <Text mt={3} fontSize="sm" color="var(--text-muted)">
              Columns required: <b>title</b>, <b>over_view</b>, <b>url</b>. Shared options below
              are applied to every video.
            </Text>
          </Box>

          {progress.total > 0 ? (
            <Box {...sectionShell}>
              {sectionLabel("Import progress")}
              <Progress
                value={progressPercent}
                size="sm"
                borderRadius="full"
                bg="var(--surface-raised)"
                sx={{ "& > div": { backgroundColor: brandPalette.primary } }}
                hasStripe={isImporting}
                isAnimated={isImporting}
              />
              <Text mt={3} fontSize="sm" fontWeight="700" color="var(--text-primary)">
                {progress.done} of {progress.total} processed · {progress.success} created ·{" "}
                {progress.failed} failed
              </Text>
            </Box>
          ) : null}

          {rows.length > 0 ? (
            <Box {...sectionShell}>
              {sectionLabel(`Sheet preview (${rows.length})`)}
              <Box overflowX="auto" maxH="280px" overflowY="auto">
                <Table size="sm" variant="simple">
                  <Thead>
                    <Tr>
                      <Th>Row</Th>
                      <Th>Title</Th>
                      <Th>Overview</Th>
                      <Th>URL</Th>
                      <Th>Status</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {rows.map((row) => (
                      <Tr key={row.id}>
                        <Td>{row.rowNumber}</Td>
                        <Td minW="180px">
                          <Input
                            size="sm"
                            value={row.title}
                            isDisabled={isImporting || row.status === "success"}
                            bg="var(--surface-raised)"
                            onChange={(e) => updateRow(row.id, { title: e.target.value })}
                          />
                        </Td>
                        <Td minW="200px">
                          <Input
                            size="sm"
                            value={row.over_view}
                            isDisabled={isImporting || row.status === "success"}
                            bg="var(--surface-raised)"
                            onChange={(e) => updateRow(row.id, { over_view: e.target.value })}
                          />
                        </Td>
                        <Td minW="220px">
                          <Input
                            size="sm"
                            value={row.url}
                            isDisabled={isImporting || row.status === "success"}
                            bg="var(--surface-raised)"
                            onChange={(e) => updateRow(row.id, { url: e.target.value })}
                          />
                        </Td>
                        <Td minW="160px">
                          <VStack align="flex-start" spacing={1}>
                            {statusBadge(row)}
                            {row.error ? (
                              <Text fontSize="xs" color="red.500">
                                {row.error}
                              </Text>
                            ) : null}
                          </VStack>
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </Box>
            </Box>
          ) : null}

          {rows.length > 0 ? (
            <>
              <Box {...sectionShell}>
                {sectionLabel("Shared options — applied to every video")}
                <SimpleGrid
                  columns={{ base: 1, md: 2 }}
                  spacingX={8}
                  spacingY={4}
                  alignItems="start"
                >
                  <MainInput
                    type="datetime-local"
                    label="Expiry date"
                    defaultValues={oneWeekFromNow}
                    error={errors?.expiry_date?.message}
                    placeholder="Expiry"
                    register={register}
                    name="expiry_date"
                  />
                  <FormControl>
                    <VStack align="stretch" spacing={2}>
                      <FormLabel
                        m={0}
                        fontSize="14px"
                        fontWeight="700"
                        color="var(--text-primary)"
                        textAlign="left"
                      >
                        Thumbnail image (optional)
                      </FormLabel>
                      <Button
                        bg={brandPalette.primary}
                        color="white"
                        borderRadius="lg"
                        position="relative"
                        h="42px"
                        fontWeight="700"
                        isDisabled={isImporting}
                        _hover={{ bg: brandPalette.accentDark }}
                      >
                        <Text noOfLines={1}>
                          {thumbLabel || "Choose thumbnail to upload"}
                        </Text>
                        <Input
                          type="file"
                          accept="image/*"
                          opacity="0"
                          position="absolute"
                          w="100%"
                          h="100%"
                          left={0}
                          top={0}
                          cursor={isImporting ? "not-allowed" : "pointer"}
                          disabled={isImporting}
                          onChange={(e) => {
                            const f = e.target.files?.[0];
                            if (!f) return;
                            setValue("thumbnail", f);
                            setThumbLabel(f.name);
                            const nextUrl = URL.createObjectURL(f);
                            if (thumbPreview) URL.revokeObjectURL(thumbPreview);
                            setThumbPreview(nextUrl);
                          }}
                        />
                      </Button>
                      {thumbPreview ? (
                        <Box
                          borderWidth="1px"
                          borderColor={brandPalette.stone}
                          borderRadius="12px"
                          overflow="hidden"
                          bg="white"
                          maxW="420px"
                        >
                          <Image
                            src={thumbPreview}
                            alt="Thumbnail preview"
                            w="100%"
                            h="180px"
                            objectFit="cover"
                          />
                        </Box>
                      ) : null}
                    </VStack>
                  </FormControl>
                </SimpleGrid>
              </Box>

              <Box {...sectionShell}>
                {sectionLabel("Course assignment")}
                <SimpleGrid
                  columns={{ base: 1, md: 2 }}
                  spacingX={8}
                  spacingY={4}
                  alignItems="start"
                >
                  <FormControl>
                    <VStack align="stretch" spacing={2}>
                      <FormLabel
                        m={0}
                        fontSize="14px"
                        fontWeight="700"
                        color="var(--text-primary)"
                        textAlign="left"
                      >
                        Chapter *
                      </FormLabel>
                      <Select
                        options={chaptersOptions}
                        onChange={(d) => {
                          if (!d) return;
                          setValue("chapter_id", d.value, { shouldDirty: true });
                        }}
                        styles={selectStyles}
                        placeholder="Select chapter"
                        isDisabled={isImporting}
                        {...selectMenuPortalProps}
                      />
                    </VStack>
                  </FormControl>
                  <FormControl>
                    <VStack align="stretch" spacing={2}>
                      <FormLabel
                        m={0}
                        fontSize="14px"
                        fontWeight="700"
                        color="var(--text-primary)"
                        textAlign="left"
                      >
                        Payment session
                      </FormLabel>
                      <Select
                        key={`payment-subject-${paymentSubjectsOptions?.length || 0}`}
                        options={paymentSubjectsOptions || []}
                        onChange={(d) => {
                          if (!d) return;
                          setValue("payment_subject_id", d.value, { shouldDirty: true });
                        }}
                        styles={selectStyles}
                        placeholder="Payment subject"
                        isLoading={!paymentSubjectsOptions}
                        isDisabled={isImporting}
                        {...selectMenuPortalProps}
                      />
                    </VStack>
                  </FormControl>
                  <FormControl gridColumn={{ base: "auto", md: "1 / -1" }}>
                    <VStack align="stretch" spacing={2}>
                      <FormLabel
                        m={0}
                        fontSize="14px"
                        fontWeight="700"
                        color="var(--text-primary)"
                        textAlign="left"
                      >
                        Groups *
                      </FormLabel>
                      <Select
                        options={groupsOptions}
                        isMulti
                        onChange={(d) => {
                          const selectedGroups = (d || []).map((val) => val.value);
                          setValue("groups", selectedGroups, { shouldDirty: true });
                        }}
                        styles={selectStyles}
                        placeholder="Select groups"
                        isDisabled={isImporting}
                        {...selectMenuPortalProps}
                        closeMenuOnSelect={false}
                      />
                    </VStack>
                  </FormControl>
                </SimpleGrid>
              </Box>

              <Box {...sectionShell}>
                {sectionLabel("Player & placement")}
                <SimpleGrid columns={{ base: 1, md: 2 }} spacingX={8} spacingY={3}>
                  <Box>
                    <FormLabel
                      m={0}
                      mb={2}
                      fontSize="14px"
                      fontWeight="700"
                      color="var(--text-primary)"
                    >
                      Player / source
                    </FormLabel>
                    <Controller
                      name="video_type"
                      control={control}
                      rules={{ required: true }}
                      render={({ field }) => (
                        <RadioGroup
                          name={field.name}
                          isDisabled={isImporting}
                          color="var(--text-primary)"
                          value={
                            field.value != null && field.value !== ""
                              ? String(field.value)
                              : "1"
                          }
                          onChange={field.onChange}
                          onBlur={field.onBlur}
                        >
                          <Flex wrap="wrap" gap={3} minH="32px" color="var(--text-primary)">
                            <Radio value="0" size="md">
                              YouTube (restricted)
                            </Radio>
                            <Radio value="1" size="md">
                              VDO
                            </Radio>
                            <Radio value="2" size="md">
                              YouTube
                            </Radio>
                            <Radio value="3" size="md">
                              Vimeo
                            </Radio>
                          </Flex>
                        </RadioGroup>
                      )}
                    />
                  </Box>
                  <Box>
                    <FormLabel
                      m={0}
                      mb={2}
                      fontSize="14px"
                      fontWeight="700"
                      color="var(--text-primary)"
                    >
                      Placement
                    </FormLabel>
                    <Controller
                      name="type"
                      control={control}
                      rules={{ required: true }}
                      render={({ field }) => (
                        <RadioGroup
                          name={field.name}
                          isDisabled={isImporting}
                          color="var(--text-primary)"
                          value={
                            field.value != null && field.value !== ""
                              ? String(field.value)
                              : "1"
                          }
                          onChange={field.onChange}
                          onBlur={field.onBlur}
                        >
                          <Flex wrap="wrap" gap={3} minH="32px" color="var(--text-primary)">
                            <Radio value="1" size="md">
                              Edutopia
                            </Radio>
                            <Radio value="7" size="md">
                              Edutopia Practice
                            </Radio>
                          </Flex>
                        </RadioGroup>
                      )}
                    />
                  </Box>
                </SimpleGrid>
              </Box>

              <Box {...sectionShell}>
                {sectionLabel("Visibility & limits")}
                <SimpleGrid columns={{ base: 1, md: 2 }} spacingX={8} spacingY={4}>
                  <Box>
                    <FormLabel
                      m={0}
                      mb={2}
                      fontSize="14px"
                      fontWeight="700"
                      color="var(--text-primary)"
                    >
                      Published
                    </FormLabel>
                    <Controller
                      name="publish"
                      control={control}
                      rules={{ required: true }}
                      render={({ field }) => (
                        <RadioGroup
                          {...field}
                          isDisabled={isImporting}
                          value={
                            field.value != null && field.value !== ""
                              ? String(field.value)
                              : undefined
                          }
                          onChange={(v) => field.onChange(v)}
                        >
                          <Stack direction="row" flexWrap="wrap" spacing={3}>
                            <Radio value="0" size="md">
                              Hidden
                            </Radio>
                            <Radio value="1" size="md">
                              Published
                            </Radio>
                          </Stack>
                        </RadioGroup>
                      )}
                    />
                  </Box>
                  <Box>
                    <FormLabel
                      m={0}
                      mb={2}
                      fontSize="14px"
                      fontWeight="700"
                      color="var(--text-primary)"
                    >
                      Homework link
                    </FormLabel>
                    <Controller
                      name="related_to_homework"
                      control={control}
                      rules={{ required: true }}
                      render={({ field }) => (
                        <RadioGroup
                          {...field}
                          isDisabled={isImporting}
                          value={
                            field.value != null && field.value !== ""
                              ? String(field.value)
                              : undefined
                          }
                          onChange={(v) => field.onChange(v)}
                        >
                          <Stack direction="row" flexWrap="wrap" spacing={3}>
                            <Radio value="1" size="md">
                              Has homework
                            </Radio>
                            <Radio value="0" size="md">
                              No homework
                            </Radio>
                          </Stack>
                        </RadioGroup>
                      )}
                    />
                  </Box>
                </SimpleGrid>

                <Box mt={4}>
                  <FormLabel
                    m={0}
                    mb={2}
                    fontSize="14px"
                    fontWeight="700"
                    color="var(--text-primary)"
                  >
                    View limits
                  </FormLabel>
                  <HStack
                    align="flex-start"
                    flexWrap="wrap"
                    gap={{ base: 3, md: 4 }}
                    spacing={0}
                  >
                    <Controller
                      name="auto_controlled"
                      control={control}
                      rules={{ required: true }}
                      render={({ field }) => (
                        <RadioGroup
                          {...field}
                          isDisabled={isImporting}
                          value={
                            field.value != null && field.value !== ""
                              ? String(field.value)
                              : undefined
                          }
                          onChange={(v) => {
                            field.onChange(v);
                            setTotalPlayed(v === "2");
                          }}
                        >
                          <Stack
                            direction="row"
                            flexWrap="wrap"
                            spacing={3}
                            flex="1"
                            minW="0"
                          >
                            <Radio value="0" size="md">
                              Not limited
                            </Radio>
                            <Radio value="1" size="md">
                              Limited views
                            </Radio>
                            <Radio value="2" size="md">
                              Controlled views
                            </Radio>
                          </Stack>
                        </RadioGroup>
                      )}
                    />
                    {totalPlayed && (
                      <Box flexShrink={0} w={{ base: "100%", sm: "140px" }}>
                        <FormLabel
                          m={0}
                          mb={1}
                          fontSize="xs"
                          fontWeight="700"
                          color="var(--text-muted)"
                        >
                          Play count
                        </FormLabel>
                        <Input
                          {...register("total_played", { valueAsNumber: true })}
                          bg="var(--surface-muted)"
                          color="var(--text-primary)"
                          borderRadius="12px"
                          borderWidth="1px"
                          borderColor="var(--border-subtle)"
                          size="sm"
                          type="number"
                          min={1}
                          placeholder="Count"
                          isDisabled={isImporting}
                        />
                      </Box>
                    )}
                  </HStack>
                </Box>

                <FormControl mt={5}>
                  <VStack align="stretch" spacing={2}>
                    <FormLabel
                      m={0}
                      fontSize="14px"
                      fontWeight="700"
                      color="var(--text-primary)"
                      textAlign="left"
                    >
                      Post-session exam (optional)
                    </FormLabel>
                    <Select
                      options={examsOptions}
                      isClearable
                      onChange={(formData) =>
                        setValue("post_exam_id", formData?.value || "", { shouldDirty: true })
                      }
                      styles={selectStyles}
                      placeholder="Select follow-up exam"
                      isDisabled={isImporting}
                      {...selectMenuPortalProps}
                    />
                  </VStack>
                </FormControl>
              </Box>

              <Flex
                justify="flex-end"
                gap={6}
                pt={4}
                borderTop="1px solid"
                borderColor="var(--border-subtle)"
              >
                <Button
                  variant="ghost"
                  color="var(--text-primary)"
                  fontWeight="800"
                  isDisabled={isImporting}
                  _hover={{ textDecoration: "underline", bg: "rgba(91, 13, 22, 0.08)" }}
                  onClick={closeFunc}
                >
                  Cancel
                </Button>
                {failedRows.length > 0 && !isImporting ? (
                  <Button
                    variant="outline"
                    borderColor={brandPalette.primary}
                    color={brandPalette.primary}
                    borderRadius="lg"
                    px={6}
                    py={6}
                    fontWeight="800"
                    onClick={handleSubmit((values) => runImport(values, failedRows))}
                  >
                    Retry {failedRows.length} failed
                  </Button>
                ) : null}
                <Button
                  type="submit"
                  bg={brandPalette.primary}
                  color="white"
                  borderRadius="lg"
                  px={8}
                  py={6}
                  fontWeight="800"
                  isLoading={isImporting}
                  isDisabled={!remainingRows.length}
                  _hover={{ bg: brandPalette.accentDark }}
                  boxShadow="0 10px 24px rgba(91, 13, 22, 0.18)"
                >
                  Import {remainingRows.length} video{remainingRows.length === 1 ? "" : "s"}
                </Button>
              </Flex>
            </>
          ) : null}
        </Stack>
      </form>
    </MainModel>
  );
};

export default BulkImportVideos;
